import React from "react";
import { collection } from "firebase/firestore";
import { FirebaseContext } from "@contexts";

import { useNavigate } from "react-router-dom";
import { WithFieldValue, DocumentData, Timestamp } from "firebase/firestore";
import { DB_COLLECTION_NAMES } from "@constants";
import { getCountFromServer, query, where } from "firebase/firestore";

import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardProps,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";

import { AuthUser, getHumanReadableStepName } from "@constants";
import { useCreateStep, useCustomToast } from "@hooks";
import { isValidStep, isFirstStep } from "@utils";
import { FormRenderer } from "./FormRenderer";

type ProductionStepEntryFormProps = CardProps & {
  productionStep: string;
  user: AuthUser;
  initialModuleId?: string | null;
};

/**
 * High level wrapper component for Production Step Forms.
 * Displays each form contained within a Chakra Card.
 * @param {string} productionStep name of the step being performed.
 */
function ProductionStepEntryForm({
  productionStep,
  user,
  initialModuleId,
  ...delegated
}: ProductionStepEntryFormProps) {
  const { createStep } = useCreateStep();
  const [errorMessage, setErrorMessage] = React.useState("");
  const [submitInProgress, setSubmitInProgress] = React.useState(false);
  const completeToast = useCustomToast(() => (
    <Alert status="success">Step entry complete!</Alert>
  ));
  const navigate = useNavigate();

  const { firestore } = React.useContext(FirebaseContext);
  const collectionName = DB_COLLECTION_NAMES.MODULES;
  const collectionRef = collection(firestore, collectionName);

  const addingFirstStep = isFirstStep(productionStep) ? true : false;
  const [moduleExists, setModuleExists] = React.useState(false);
  const [moduleId, setModuleId] = React.useState("");

  const doCreate = React.useCallback(
    async (data: WithFieldValue<DocumentData>) => {
      setErrorMessage("");
      setSubmitInProgress(true);
      let doCreateStep = true;
      if (addingFirstStep) {
        setModuleId(data.moduleId);
        const queryResult = query(
          collectionRef,
          where("id", "==", data.moduleId),
        );
        const count = (await getCountFromServer(queryResult)).data().count;
        if (count > 0) {
          setModuleExists(true);
          doCreateStep = false;
        }

        const serialsResult = query(
          collectionRef,
          where("serials", "array-contains", data.moduleId),
        );
        const countSerials = (await getCountFromServer(serialsResult)).data()
          .count;
        if (countSerials > 0) {
          setModuleExists(true);
          doCreateStep = false;
        }
      }

      if (doCreateStep) {
        try {
          // Submit Step
          await createStep({
            moduleId: "",
            details: {},
            stepName: productionStep,
            timestamp: Timestamp.now(),
            // Entry will fail without user information.
            userId: user?.firebaseUser?.uid || "",
            userName: user ? `${user.firstName} ${user.lastName}` : "Unknown",
            // Over write all unspecified fields
            ...data,
          });
          // Display Success
          completeToast();
          // Redirect to the module page.
          setTimeout(() => navigate(`/modules/${data.moduleId}`), 2000);
        } catch (ex) {
          setErrorMessage((ex as Error).message);
        } finally {
          setSubmitInProgress(false);
        }
      }
    },
    [createStep, user, productionStep],
  );

  let cardContent = (
    <>
      <CardHeader>
        <Heading>{getHumanReadableStepName(productionStep)}</Heading>
      </CardHeader>
      <CardBody>
        <FormRenderer
          submit={doCreate}
          disabled={submitInProgress}
          stepName={productionStep}
          initialModuleId={initialModuleId}
        />
      </CardBody>
      {errorMessage ? (
        <CardFooter as={VStack}>
          <Text color="danger">{errorMessage}</Text>
        </CardFooter>
      ) : undefined}
    </>
  );

  if (moduleExists) {
    cardContent = (
      <>
        <CardHeader>
          <Heading>Uh Oh!</Heading>
        </CardHeader>
        <CardBody>
          <Text>
            {`It looks like the module already exists. Please double check that ${moduleId} is the correct module ID.`}
          </Text>
        </CardBody>
      </>
    );
  }

  if (!isValidStep(productionStep)) {
    cardContent = (
      <>
        <CardHeader>
          <Heading>Uh Oh!</Heading>
        </CardHeader>
        <CardBody>
          <Text>
            {`Looks like there's not a step for "${getHumanReadableStepName(
              productionStep,
            )}". Please double check this is the correct step.`}
          </Text>
        </CardBody>
      </>
    );
  }
  return (
    <Card width="800px" {...delegated}>
      {cardContent}
    </Card>
  );
}

export { ProductionStepEntryForm };
